/**
 * Irandre · Collections
 * - <ir-collection>: live filtering & sorting (Section Rendering API), sticky capsule toolbar,
 *   layout switch, "load more", back-navigation restore and Shopify collection analytics.
 * - Product cards: colour swatches that preview/select the variant image, desktop quick-add
 *   panel (sizes), touch/keyboard quick-add sheet, swipe-gallery progress, deferred extra images.
 * - Recently viewed rail and the /collections product rail.
 * Card behaviour is delegated at document level, so cards added later (filters, pages) just work.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const mobileQuery = window.matchMedia('(max-width: 749px)');
const IMG_WIDTHS = [360, 480, 640, 820, 1100];

/* ---------- Theme modules (loaded lazily; the page still works without them) ---------- */
let scrollModule = null;
const scrollReady = import('@theme/scroll-container')
  .then((m) => (scrollModule = m))
  .catch(() => null);
let eventsModule = null;
const eventsReady = import('@shopify/events')
  .then((m) => (eventsModule = m))
  .catch(() => null);

const scroller = () =>
  scrollModule?.getScrollContainer?.() ?? document.scrollingElement ?? document.documentElement;
const scrollTopNow = () => scroller().scrollTop;
const scrollToTop = (top, smooth = true) =>
  scroller().scrollTo({ top, behavior: smooth && !reduceMotion.matches ? 'smooth' : 'instant' });
const headerHeight = () =>
  parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) ||
  document.querySelector('header-component')?.offsetHeight ||
  0;

/* ---------- Strings ---------- */
let i18nCache = null;
function i18n() {
  if (i18nCache) return i18nCache;
  try {
    i18nCache = JSON.parse(document.querySelector('[data-ir-i18n]')?.textContent || '{}');
  } catch (_) {
    i18nCache = {};
  }
  return i18nCache;
}
const t = (key, fallback = '') => i18n()[key] ?? fallback;

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

/** Price markup with screen-reader context, matching the Liquid card. */
function priceHtml(price, compare) {
  if (!price) return '';
  if (compare) {
    return `<span class="visually-hidden">${escapeHtml(t('salePrice', 'Sale price'))}</span><span class="ir-pcard__now is-sale">${escapeHtml(price)}</span><span class="visually-hidden">${escapeHtml(t('regularPrice', 'Regular price'))}</span><s class="ir-pcard__was">${escapeHtml(compare)}</s>`;
  }
  return `<span class="visually-hidden">${escapeHtml(t('price', 'Price'))}</span><span>${escapeHtml(price)}</span>`;
}

/* ---------- Storage helpers (never throw) ---------- */
const store = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch (_) {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (_) {
      /* private mode */
    }
  },
  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (_) {
      /* private mode */
    }
  },
};

/* ==========================================================================
   Cart
   ========================================================================== */
function cartDrawer() {
  return document.getElementById('cart-drawer');
}
/** Horizon opens the drawer itself when the merchant enabled "auto open". */
function drawerAutoOpens() {
  return Boolean(document.querySelector('cart-drawer-component[auto-open]'));
}
function openCartDrawer() {
  const drawer = cartDrawer();
  if (!drawer || typeof drawer.open !== 'function') return false;
  if (!drawer.isOpen) drawer.open();
  return true;
}

async function addToCart(variantId, source, { productId = '', openCart = true } = {}) {
  await eventsReady;
  const routes = (window.Theme && window.Theme.routes) || {};
  const CartLinesUpdateEvent = eventsModule?.CartLinesUpdateEvent;
  const CartErrorEvent = eventsModule?.CartErrorEvent;

  const body = new FormData();
  body.append('id', String(variantId));
  body.append('quantity', '1');
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent?.createPromise?.();
  const emitter = source?.isConnected ? source : document.body;
  if (deferred) {
    emitter.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body,
    }).then((r) => r.json());
    const failed = Boolean(response.status);
    const cart = await fetch(`${routes.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((r) => r.json());

    deferred?.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'irandre-collection',
        sourceId: String(productId),
        itemCount: 1,
        productId: String(productId),
        sections: response.sections,
        didError: failed,
      },
    });

    if (failed) {
      if (CartErrorEvent) {
        emitter.dispatchEvent(
          new CartErrorEvent({
            error: response.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: response.description },
          })
        );
      }
      return { ok: false, message: response.description || response.message || t('error') };
    }

    if (openCart && !drawerAutoOpens()) {
      (deferred ? deferred.promise : Promise.resolve())
        .then(() =>
          requestAnimationFrame(() => {
            if (!openCartDrawer() && !deferred) window.location.href = routes.cart_url || '/cart';
          })
        )
        .catch(() => {});
    }
    return { ok: true, cart };
  } catch (error) {
    if (deferred) {
      try {
        deferred.reject(error);
      } catch (_) {
        /* already settled */
      }
    }
    return { ok: false, message: t('error') };
  }
}

/* ==========================================================================
   Product data & variants
   ========================================================================== */
const SIZE_RANK = { xxs: 0, xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 6, '2xl': 6, xxxl: 7, '3xl': 7, '4xl': 8, '5xl': 9 };
function sizeRank(value, index) {
  const v = String(value).toLowerCase().trim();
  if (v in SIZE_RANK) return SIZE_RANK[v];
  if (v.includes('one size') || v === 'os') return 900;
  const n = parseFloat(v.replace(',', '.'));
  if (!Number.isNaN(n)) return 100 + n;
  return 500 + index;
}

function cardData(card) {
  if (card._irData !== undefined) return card._irData;
  const node = card.querySelector(':scope > script[data-ir-pdata]');
  let data = null;
  try {
    data = node ? JSON.parse(node.textContent) : null;
  } catch (_) {
    data = null;
  }
  if (data) {
    data.variants = data.variants.map(([id, options, available, price, compare, saving]) => ({
      id,
      options,
      available: Boolean(available),
      price,
      compare,
      saving: Number(saving) || 0,
    }));
    data.selected = data.color;
  }
  card._irData = data;
  return data;
}

function colorValues(data) {
  return data && data.ci >= 0 ? data.options[data.ci].values : [];
}
function sizeValues(data) {
  if (!data || data.si < 0) return [];
  return data.options[data.si].values
    .map((value, index) => ({ value, rank: sizeRank(value, index) }))
    .sort((a, b) => a.rank - b.rank)
    .map((s) => s.value);
}
function needsColor(data, color = data?.selected) {
  return Boolean(data && data.ci >= 0 && colorValues(data).length > 1 && !color);
}
function matchVariant(data, { color, size } = {}) {
  return data.variants.filter(
    (v) =>
      (data.ci < 0 || color == null || v.options[data.ci] === color) &&
      (data.si < 0 || size == null || v.options[data.si] === size)
  );
}
function colorAvailable(data, color) {
  return matchVariant(data, { color }).some((v) => v.available);
}
function bestVariant(data, color) {
  const pool = matchVariant(data, { color });
  return pool.find((v) => v.available) || pool[0] || data.variants.find((v) => v.available) || data.variants[0];
}
function colorImage(data, color) {
  const index = colorValues(data).indexOf(color);
  return index >= 0 ? data.colorImages?.[index] || '' : '';
}
function srcset(url) {
  if (!url) return '';
  const join = url.includes('?') ? '&' : '?';
  return IMG_WIDTHS.map((w) => `${url}${join}width=${w} ${w}w`).join(', ');
}
function sized(url, width) {
  if (!url) return '';
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

/* ==========================================================================
   Card behaviour
   ========================================================================== */
function mainImage(card) {
  return card.querySelector('.ir-pcard__slide--main .ir-pcard__img');
}

/** Deferred gallery images carry data-ir-srcset until they are needed. */
function hydrate(img) {
  if (!img || !img.dataset.irSrcset) return;
  img.srcset = img.dataset.irSrcset;
  img.src = img.dataset.irSrc || '';
  delete img.dataset.irSrcset;
  delete img.dataset.irSrc;
}
function hydrateCard(card, all = false) {
  const selector = all ? '.ir-pcard__slide img[data-ir-srcset]' : '.ir-pcard__slide--alt img[data-ir-srcset]';
  card.querySelectorAll(selector).forEach(hydrate);
}

/**
 * Swaps the main card image. Each call supersedes earlier ones (token), so a slow preload can never
 * land after the shopper has moved on and leave a colour on screen that is neither hovered nor picked.
 */
function showImage(card, url) {
  const img = mainImage(card);
  if (!img || img.tagName !== 'IMG') return;
  if (!img.dataset.irOriginalSrc) {
    img.dataset.irOriginalSrc = img.currentSrc || img.src;
    img.dataset.irOriginalSrcset = img.getAttribute('srcset') || '';
  }
  const token = (card._irImgToken = (card._irImgToken || 0) + 1);
  const nextSrcset = url ? srcset(url) : img.dataset.irOriginalSrcset;
  const nextSrc = url ? sized(url, 820) : img.dataset.irOriginalSrc;
  card.classList.toggle('is-variant', Boolean(url));
  const track = card.querySelector('[data-ir-track]');
  if (track && track.scrollLeft > 0) track.scrollTo({ left: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  if (img.getAttribute('srcset') === nextSrcset) {
    img.classList.remove('is-swapping');
    return;
  }

  const swap = () => {
    if (token !== card._irImgToken) return;
    img.setAttribute('srcset', nextSrcset);
    img.src = nextSrc;
    requestAnimationFrame(() => img.classList.remove('is-swapping'));
  };
  if (reduceMotion.matches) return swap();
  const preload = new Image();
  preload.sizes = img.sizes;
  preload.srcset = nextSrcset;
  preload.src = nextSrc;
  img.classList.add('is-swapping');
  if (preload.complete) swap();
  else {
    preload.onload = swap;
    preload.onerror = swap;
  }
}

function previewColor(card, color) {
  const data = cardData(card);
  if (!data) return;
  showImage(card, colorImage(data, color));
}

function restoreColor(card) {
  const data = cardData(card);
  if (!data) return;
  if (card.dataset.irPicked && data.selected) showImage(card, colorImage(data, data.selected));
  else showImage(card, '');
}

function renderCardPrice(card, variant) {
  const price = card.querySelector('[data-ir-price]');
  if (!price || !variant || price.querySelector('.ir-pcard__from')) return;
  price.innerHTML = priceHtml(variant.price, variant.compare);
  renderSaleFlag(card, variant);
}

/* One flag at most (Sold out > Sale > merchant badge), kept in step with the chosen colour's price. */
function renderSaleFlag(card, variant) {
  const flags = card.querySelector('.ir-pcard__flags');
  if (!flags || flags.querySelector('.ir-pcard__flag--muted')) return;
  const onSale = Boolean(variant.compare);
  let flag = flags.querySelector('.ir-pcard__flag--sale');
  if (onSale && !flag) {
    flag = document.createElement('span');
    flag.className = 'ir-pcard__flag ir-pcard__flag--sale';
    flags.prepend(flag);
  }
  if (flag) {
    flag.hidden = !onSale;
    if (onSale) flag.innerHTML = `<span class="visually-hidden">${escapeHtml(t('sale', 'Sale'))}</span>−${variant.saving}%`;
  }
  flags.querySelectorAll('.ir-pcard__flag:not(.ir-pcard__flag--sale)').forEach((badge) => (badge.hidden = onSale));
}

function selectColor(card, color) {
  const data = cardData(card);
  card.querySelectorAll('[data-ir-swatch]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.irSwatch === color)));
  card.dataset.irPicked = '1';
  if (!data) return;
  data.selected = color;
  showImage(card, colorImage(data, color));
  const variant = bestVariant(data, color);
  if (variant) {
    const href = `${data.url}${data.url.includes('?') ? '&' : '?'}variant=${variant.id}`;
    card.querySelectorAll('[data-ir-link]').forEach((a) => a.setAttribute('href', href));
    renderCardPrice(card, variant);
  }
  if (card._irPanelBuilt) buildPanel(card, true);
}

function markSoldOutSwatches(card) {
  const data = cardData(card);
  if (!data || data.ci < 0) return;
  card.querySelectorAll('[data-ir-swatch]').forEach((button) => {
    const value = button.dataset.irSwatch;
    const soldOut = !colorAvailable(data, value);
    button.classList.toggle('is-soldout', soldOut);
    button.setAttribute('aria-label', soldOut ? `${value} — ${t('soldOut', 'Sold out')}` : value);
  });
}

/* Desktop quick-add panel (pointer only; keyboard users get the "+" → sheet) */
function buildPanel(card, force = false) {
  const panel = card.querySelector('[data-ir-qa-panel]');
  if (!panel || (card._irPanelBuilt && !force)) return;
  const data = cardData(card);
  if (!data || data.mode === 'none') return;
  card._irPanelBuilt = true;
  panel.setAttribute('aria-hidden', 'true');
  panel.replaceChildren();
  delete panel.dataset.state;

  if (needsColor(data)) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ir-qa__add';
    button.tabIndex = -1;
    button.dataset.irQaOpen = '';
    button.innerHTML = `<span>${escapeHtml(data.mode === 'size' ? t('chooseColorSize', 'Choose color & size') : t('chooseColor', 'Choose a color'))}</span><span class="ir-qa__add-icon" aria-hidden="true">+</span>`;
    panel.append(button);
    return;
  }

  if (data.mode === 'size') {
    const label = document.createElement('p');
    label.className = 'ir-qa__label';
    const color = data.ci >= 0 && colorValues(data).length > 1 ? data.selected : '';
    label.innerHTML = `<span>${escapeHtml(t('quickAdd', 'Quick add'))}</span>${color ? `<b>${escapeHtml(color)}</b>` : ''}`;
    const row = document.createElement('div');
    row.className = 'ir-qa__sizes';
    sizeValues(data).forEach((size) => {
      const variant = matchVariant(data, { color: data.ci >= 0 ? data.selected : null, size }).find((v) => v.available);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ir-qa__size';
      button.tabIndex = -1;
      button.textContent = size;
      if (variant) {
        button.dataset.irAdd = variant.id;
        button.title = t('addSizeAria', 'Add size __SIZE__ to bag').replace('__SIZE__', size);
      } else {
        button.disabled = true;
        button.title = t('soldOut', 'Sold out');
      }
      row.append(button);
    });
    panel.append(label, row);
  } else {
    const variant = bestVariant(data, data.selected);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ir-qa__add';
    button.tabIndex = -1;
    if (variant && variant.available) button.dataset.irAdd = variant.id;
    else button.disabled = true;
    button.innerHTML = `<span data-ir-add-label>${escapeHtml(variant?.available ? t('addToBag', 'Add to bag') : t('soldOut', 'Sold out'))}</span><span class="ir-qa__add-icon" aria-hidden="true">+</span>`;
    panel.append(button);
  }
}

function setState(el, state, ms = 2200) {
  if (!el) return;
  if (state) el.dataset.state = state;
  else delete el.dataset.state;
  // A swatch click can rebuild the panel mid-add; a detached button must not pin the new one open.
  const panel = el.closest('[data-ir-qa-panel]');
  if (panel && panel.contains(el)) {
    if (state) panel.dataset.state = state;
    else delete panel.dataset.state;
  }
  if (state === 'done' || state === 'error') {
    clearTimeout(el._irTimer);
    el._irTimer = setTimeout(() => setState(el, null), ms);
  }
}

async function quickAddFromPanel(button) {
  if (button.dataset.state === 'busy') return;
  const card = button.closest('[data-ir-card]');
  const data = card && cardData(card);
  const label = button.querySelector('[data-ir-add-label]');
  setState(button, 'busy');
  if (label) label.textContent = t('adding', 'Adding…');
  const result = await addToCart(button.dataset.irAdd, button, {
    productId: data?.id,
    openCart: t('openCart', true) !== false,
  });
  setState(button, result.ok ? 'done' : 'error');
  if (label) {
    label.textContent = result.ok ? `${t('added', 'Added')} ✓` : t('error', 'Try again');
    setTimeout(() => (label.textContent = t('addToBag', 'Add to bag')), 2200);
  }
}

/* Touch gallery progress */
function syncDots(track) {
  const card = track.closest('[data-ir-card]');
  const dots = card?.querySelectorAll('.ir-pcard__dot');
  if (!dots?.length) return;
  const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
  dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
}

function enhanceCards(root = document) {
  root.querySelectorAll('[data-ir-card]').forEach((card) => {
    if (card._irReady) return;
    card._irReady = true;
    markSoldOutSwatches(card);
    // Touch: the swipe gallery needs its slides (they stay loading="lazy").
    if (!finePointer.matches) hydrateCard(card, true);
  });
}

/* ---------- Delegated card events ---------- */
document.addEventListener('pointerover', (event) => {
  if (!finePointer.matches || !(event.target instanceof Element)) return;
  const card = event.target.closest('[data-ir-card]');
  if (!card) return;
  if (!card._irHydrated) {
    card._irHydrated = true;
    hydrateCard(card);
  }
  if (!card._irPanelBuilt) buildPanel(card);
  const swatch = event.target.closest('[data-ir-swatch]');
  if (swatch) previewColor(card, swatch.dataset.irSwatch);
});

document.addEventListener('focusin', (event) => {
  const card = event.target instanceof Element ? event.target.closest('[data-ir-card]') : null;
  if (card && !card._irHydrated && finePointer.matches) {
    card._irHydrated = true;
    hydrateCard(card);
  }
});

document.addEventListener('pointerout', (event) => {
  if (!finePointer.matches || !(event.target instanceof Element)) return;
  const group = event.target.closest('.ir-pcard__swatches');
  if (!group) return;
  if (event.relatedTarget instanceof Node && group.contains(event.relatedTarget)) return;
  const card = group.closest('[data-ir-card]');
  if (card) restoreColor(card);
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const swatch = event.target.closest('[data-ir-swatch]');
  if (swatch) {
    const card = swatch.closest('[data-ir-card]');
    if (card) selectColor(card, swatch.dataset.irSwatch);
    return;
  }

  const add = event.target.closest('[data-ir-qa-panel] [data-ir-add]');
  if (add) {
    event.preventDefault();
    quickAddFromPanel(add);
    return;
  }

  const open = event.target.closest('[data-ir-qa-open]');
  if (open) {
    event.preventDefault();
    const card = open.closest('[data-ir-card]');
    if (card) sheet.open(card, open);
    return;
  }

  const arrow = event.target.closest('[data-ir-rail-prev], [data-ir-rail-next]');
  if (arrow) {
    if (arrow.getAttribute('aria-disabled') === 'true') return;
    const host = arrow.closest('.ir-cindex__loved, [data-ir-rail-host]');
    const track = host?.querySelector('[data-ir-rail-track]');
    if (track) {
      const dir = arrow.hasAttribute('data-ir-rail-next') ? 1 : -1;
      track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
  }
});

document.addEventListener(
  'scroll',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-ir-track]')) {
      if (target._irRaf) return;
      target._irRaf = requestAnimationFrame(() => {
        target._irRaf = 0;
        syncDots(target);
      });
    } else if (target.matches('[data-ir-rail-track]')) {
      syncRailArrows(target);
    }
  },
  { capture: true, passive: true }
);

function syncRailArrows(track) {
  const host = track.closest('.ir-cindex__loved, [data-ir-rail-host]');
  if (!host) return;
  const max = track.scrollWidth - track.clientWidth - 2;
  const prev = host.querySelector('[data-ir-rail-prev]');
  const next = host.querySelector('[data-ir-rail-next]');
  // aria-disabled (not disabled) keeps a focused arrow focusable at the end of the rail.
  if (prev) prev.setAttribute('aria-disabled', String(track.scrollLeft <= 2));
  if (next) next.setAttribute('aria-disabled', String(track.scrollLeft >= max));
}

/* ==========================================================================
   Quick-add sheet (touch, keyboard and any card that needs a choice)
   ========================================================================== */
const sheet = {
  get el() {
    return document.querySelector('[data-ir-qa-sheet]');
  },

  open(card, trigger) {
    const dialog = this.el;
    const data = cardData(card);
    if (!dialog || !data || data.mode === 'none') {
      const link = card.querySelector('[data-ir-link]');
      if (link) window.location.href = link.href;
      return;
    }
    this.card = card;
    // Never hand focus back into the aria-hidden hover panel.
    this.trigger = trigger?.closest('[aria-hidden="true"]')
      ? card.querySelector('.ir-pcard__plus') || card.querySelector('.ir-pcard__title a')
      : trigger;
    this.data = data;
    this.color = data.ci >= 0 ? data.selected : null;
    this.size = null;

    dialog.querySelector('[data-ir-qa-title]').innerHTML = `${escapeHtml(data.name)}${
      data.sub ? `<small>${escapeHtml(data.sub)}</small>` : ''
    }`;
    dialog.querySelector('[data-ir-qa-link]').href = card.querySelector('[data-ir-link]')?.getAttribute('href') || data.url;
    this.renderBody();
    this.update();
    this.message('');

    if (!dialog._irBound) {
      dialog._irBound = true;
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
        if (event.target.closest?.('[data-ir-qa-close]')) dialog.close();
      });
      dialog.addEventListener('close', () => {
        const target = this.returnFocus === false ? null : this.trigger;
        this.returnFocus = true;
        if (target?.isConnected) target.focus({ preventScroll: true });
      });
      dialog.querySelector('[data-ir-qa-add]').addEventListener('click', () => this.add());
      const body = dialog.querySelector('[data-ir-qa-body]');
      body.addEventListener('click', (event) => {
        const option = event.target.closest('[data-ir-qa-color], [data-ir-qa-size]');
        if (!option || option.disabled) return;
        if (option.hasAttribute('data-ir-qa-color')) {
          this.color = option.dataset.irQaColor;
          if (this.size && !matchVariant(this.data, { color: this.color, size: this.size }).some((v) => v.available)) this.size = null;
          this.syncColors();
          this.renderSizes();
        } else {
          this.size = option.dataset.irQaSize;
          this.syncSizes();
        }
        this.update();
      });
      body.addEventListener('keydown', (event) => this.radioKeys(event));
    }

    dialog.showModal();
  },

  radioKeys(event) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    const group = event.target.closest('[role="radiogroup"]');
    if (!group) return;
    const items = [...group.querySelectorAll('[role="radio"]:not(:disabled)')];
    const index = items.indexOf(event.target);
    if (index < 0) return;
    event.preventDefault();
    const next = items[(index + keys[event.key] + items.length) % items.length];
    next.click();
    next.focus();
  },

  renderBody() {
    const { data } = this;
    const body = this.el.querySelector('[data-ir-qa-body]');
    body.replaceChildren();
    const colors = colorValues(data);

    if (colors.length > 1) {
      const wrap = document.createElement('div');
      wrap.innerHTML = `<p class="ir-qa-opt__label" id="IrQaColorLabel">${escapeHtml(t('color', 'Color'))}: <b></b></p>`;
      const group = document.createElement('div');
      group.className = 'ir-qa-opt__colors';
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-labelledby', 'IrQaColorLabel');
      const sourceSwatches = this.card.querySelectorAll('[data-ir-swatch]');
      const styleFor = (value) => {
        for (const s of sourceSwatches) if (s.dataset.irSwatch === value) return s.getAttribute('style') || '';
        const img = colorImage(data, value);
        return img ? `--sw: url('${sized(img, 80)}') center / cover;` : '';
      };
      colors.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ir-qa-opt__color';
        button.setAttribute('role', 'radio');
        button.title = value;
        button.dataset.irQaColor = value;
        button.setAttribute('style', styleFor(value));
        if (!colorAvailable(data, value)) {
          button.classList.add('is-soldout');
          button.setAttribute('aria-label', `${value} — ${t('soldOut', 'Sold out')}`);
        } else {
          button.setAttribute('aria-label', value);
        }
        group.append(button);
      });
      wrap.append(group);
      body.append(wrap);
      this.syncColors();
    }

    if (data.mode === 'size') {
      const wrap = document.createElement('div');
      wrap.dataset.irQaSizes = '';
      body.append(wrap);
      this.renderSizes();
    }
  },

  syncColors() {
    const colors = [...this.el.querySelectorAll('[data-ir-qa-color]')];
    colors.forEach((b, i) => {
      const on = b.dataset.irQaColor === this.color;
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on || (!this.color && i === 0) ? 0 : -1;
    });
    const label = this.el.querySelector('#IrQaColorLabel b');
    if (label) label.textContent = this.color || t('chooseColor', 'Choose a color');
  },

  renderSizes() {
    const wrap = this.el.querySelector('[data-ir-qa-sizes]');
    if (!wrap) return;
    const { data } = this;
    wrap.innerHTML = `<p class="ir-qa-opt__label" id="IrQaSizeLabel">${escapeHtml(t('size', 'Size'))}</p>`;
    const group = document.createElement('div');
    group.className = 'ir-qa-opt__sizes';
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-labelledby', 'IrQaSizeLabel');
    sizeValues(data).forEach((size) => {
      const available = matchVariant(data, { color: this.color, size }).some((v) => v.available);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ir-qa-opt__size';
      button.setAttribute('role', 'radio');
      button.dataset.irQaSize = size;
      button.textContent = size;
      if (!available) {
        button.disabled = true;
        button.setAttribute('aria-label', `${size} — ${t('soldOut', 'Sold out')}`);
      }
      group.append(button);
    });
    wrap.append(group);
    this.syncSizes();
  },

  syncSizes() {
    const sizes = [...this.el.querySelectorAll('[data-ir-qa-size]')];
    const firstEnabled = sizes.find((b) => !b.disabled);
    sizes.forEach((b) => {
      const on = b.dataset.irQaSize === this.size;
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on || (!this.size && b === firstEnabled) ? 0 : -1;
    });
  },

  current() {
    const { data } = this;
    if (needsColor(data, this.color)) return null;
    if (data.mode === 'size') {
      if (!this.size) return null;
      return matchVariant(data, { color: this.color, size: this.size }).find((v) => v.available) || null;
    }
    const variant = bestVariant(data, this.color);
    return variant && variant.available ? variant : null;
  },

  update() {
    const dialog = this.el;
    const { data } = this;
    const variant = this.current() || bestVariant(data, this.color);
    const img = dialog.querySelector('[data-ir-qa-img]');
    const imageUrl = colorImage(data, this.color) || data.image;
    if (imageUrl) {
      img.src = sized(imageUrl, 240);
      img.srcset = `${sized(imageUrl, 160)} 1x, ${sized(imageUrl, 320)} 2x`;
    }
    const price = dialog.querySelector('[data-ir-qa-price]');
    if (variant) price.innerHTML = priceHtml(variant.price, variant.compare);

    const add = dialog.querySelector('[data-ir-qa-add]');
    const addLabel = dialog.querySelector('[data-ir-qa-add-label]');
    const ready = this.current();
    add.disabled = !ready;
    if (ready) addLabel.textContent = `${t('addToBag', 'Add to bag')} — ${ready.price}`;
    else if (needsColor(data, this.color)) addLabel.textContent = t('chooseColor', 'Choose a color');
    else if (data.mode === 'size') addLabel.textContent = t('selectSize', 'Select a size');
    else addLabel.textContent = t('soldOut', 'Sold out');
  },

  message(text, ok = false) {
    const msg = this.el.querySelector('[data-ir-qa-msg]');
    msg.textContent = '';
    msg.classList.toggle('is-ok', ok);
    // Set on the next frame so identical consecutive messages are still announced.
    if (text) requestAnimationFrame(() => (msg.textContent = text));
  },

  async add() {
    const variant = this.current();
    const add = this.el.querySelector('[data-ir-qa-add]');
    if (!variant || add.dataset.state === 'busy') return;
    setState(add, 'busy');
    add.querySelector('[data-ir-qa-add-label]').textContent = t('adding', 'Adding…');
    const openCart = t('openCart', true) !== false;
    // Dispatched from inside the modal so Horizon's drawer waits for the sheet to close.
    const result = await addToCart(variant.id, add, { productId: this.data.id, openCart: false });
    if (!result.ok) {
      setState(add, 'error');
      this.message(result.message || t('error'));
      this.update();
      return;
    }
    setState(add, 'done');
    add.querySelector('[data-ir-qa-add-label]').textContent = `${t('added', 'Added')} ✓`;
    this.message(t('addedMsg', 'Added to your bag.'), true);
    const plus = this.card?.querySelector('[data-ir-qa-open]');
    if (plus) setState(plus, 'done');
    setTimeout(() => {
      const opensDrawer = openCart && !drawerAutoOpens();
      this.returnFocus = !opensDrawer;
      this.el.close();
      setState(add, null);
      if (opensDrawer) openCartDrawer();
    }, 650);
  },
};

/* ==========================================================================
   <ir-collection>
   ========================================================================== */
class IrCollection extends HTMLElement {
  connectedCallback() {
    this.connection?.abort();
    this.connection = new AbortController();
    const signal = this.connection.signal;

    this.sectionId = this.dataset.sectionId;
    this.toolbar = this.querySelector('[data-ir-toolbar]');
    this.drawer = this.querySelector('[data-ir-drawer]');
    this.form = this.querySelector('[data-ir-filter-form]');
    this.requestId = 0;
    this.dirtyWhileOpen = false;

    this.initDensity();
    this.initSort(signal);
    this.initDrawer(signal);
    this.initSticky(signal);
    this.initCountFit(signal);
    enhanceCards(this);

    this.addEventListener('click', this.onClick, { signal });
    this.form?.addEventListener('change', this.onFormChange, { signal });
    this.form?.addEventListener('input', this.onFormInput, { signal });
    this.form?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.apply(this.formUrl());
      },
      { signal }
    );
    // A hidden (scrolled-away) toolbar must come back the moment it receives focus.
    this.toolbar?.addEventListener('focusin', () => this.toolbar.removeAttribute('data-hidden'), { signal });

    this.restorePages();
  }

  /* ---------- Toolbar count: a clipped "2…" would read as a wrong number, so it is hidden instead ---------- */
  initCountFit(signal) {
    const count = this.querySelector('[data-ir-count]');
    if (!count) return;
    this.fitCount();
    if (!('ResizeObserver' in window)) return;
    const observer = new ResizeObserver(() => this.fitCount());
    observer.observe(count);
    signal.addEventListener('abort', () => observer.disconnect());
  }

  fitCount() {
    const count = this.querySelector('[data-ir-count]');
    // Clipped, not display:none, so the polite live region keeps announcing result counts.
    if (count) count.classList.toggle('is-tight', count.scrollWidth > count.clientWidth + 1);
  }

  disconnectedCallback() {
    this.connection?.abort();
    this.stickyObserver?.disconnect();
    this.controller?.abort();
  }

  /* ---------- Layout switch ---------- */
  initDensity() {
    // The inline script in the section already applied any saved preference before first paint.
    if (!this.dataset.viewD) this.dataset.viewD = this.dataset.colsDesktop || '4';
    if (!this.dataset.viewM) this.dataset.viewM = this.dataset.colsMobile || '2';
    this.syncDensity();
  }

  sizesAttr() {
    const d = this.dataset.viewD === '3' ? ['548px', '30vw'] : ['402px', '23vw'];
    const m = this.dataset.viewM === '1' ? '92vw' : '46vw';
    return `(min-width: 1808px) ${d[0]}, (min-width: 990px) ${d[1]}, (min-width: 750px) 30vw, ${m}`;
  }

  syncDensity() {
    this.querySelectorAll('[data-ir-cols]').forEach((b) => {
      const cols = b.dataset.irCols;
      const current = cols === '1' || cols === '2' ? this.dataset.viewM : this.dataset.viewD;
      b.setAttribute('aria-pressed', String(cols === current));
    });
    const sizes = this.sizesAttr();
    this.querySelectorAll('[data-ir-results] .ir-pcard__img, [data-ir-results] .ir-tile__img').forEach((img) => {
      if (img.tagName === 'IMG' && img.getAttribute('sizes') !== sizes) img.setAttribute('sizes', sizes);
    });
  }

  setDensity(cols) {
    const mobile = cols === '1' || cols === '2';
    const apply = () => {
      if (mobile) {
        this.dataset.viewM = cols;
        store.set('ir:cols:m', cols);
      } else {
        this.dataset.viewD = cols;
        store.set('ir:cols:d', cols);
      }
      this.syncDensity();
    };
    if (document.startViewTransition && !reduceMotion.matches) document.startViewTransition(apply);
    else apply();
  }

  /* ---------- Sort menu ---------- */
  initSort(signal) {
    this.sortButton = this.querySelector('[data-ir-sort-btn]');
    this.sortMenu = this.querySelector('[data-ir-sort-menu]');
    if (!this.sortButton || !this.sortMenu) return;
    this.sortButton.addEventListener('click', () => (this.sortMenu.hidden ? this.openSort() : this.closeSort()), { signal });
    this.sortButton.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.openSort(event.key === 'ArrowUp' ? 'last' : 'checked');
        }
      },
      { signal }
    );
    this.sortMenu.addEventListener(
      'keydown',
      (event) => {
        const items = [...this.sortMenu.querySelectorAll('[role="menuitemradio"]')];
        const index = items.indexOf(document.activeElement);
        const go = (i) => items[(i + items.length) % items.length]?.focus();
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          go(index + 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          go(index - 1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          go(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          go(items.length - 1);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          this.closeSort(true);
        } else if (event.key === 'Tab') this.closeSort();
      },
      { signal }
    );
    this.sortMenu.addEventListener(
      'click',
      (event) => {
        const option = event.target.closest('[data-ir-sort-value]');
        if (!option) return;
        const radio = this.form?.querySelector(`input[name="sort_by"][value="${CSS.escape(option.dataset.irSortValue)}"]`);
        if (radio) radio.checked = true;
        this.closeSort(true);
        this.apply(this.formUrl(option.dataset.irSortValue));
      },
      { signal }
    );
    this.outsideSort = (event) => {
      if (!this.querySelector('[data-ir-sort]')?.contains(event.target)) this.closeSort();
    };
  }

  openSort(focus = 'checked') {
    this.sortMenu.hidden = false;
    this.sortButton.setAttribute('aria-expanded', 'true');
    const items = [...this.sortMenu.querySelectorAll('[role="menuitemradio"]')];
    const target = focus === 'last' ? items[items.length - 1] : items.find((i) => i.getAttribute('aria-checked') === 'true') || items[0];
    requestAnimationFrame(() => target?.focus());
    document.addEventListener('pointerdown', this.outsideSort);
  }

  closeSort(returnFocus = false) {
    if (!this.sortMenu || this.sortMenu.hidden) return;
    this.sortMenu.hidden = true;
    this.sortButton.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', this.outsideSort);
    if (returnFocus) this.sortButton.focus();
  }

  /* ---------- Drawer ---------- */
  initDrawer(signal) {
    if (!this.drawer) return;
    this.drawer.addEventListener(
      'click',
      (event) => {
        if (event.target === this.drawer) this.closeDrawer();
      },
      { signal }
    );
    this.drawer.addEventListener(
      'close',
      () => {
        this.toolbar?.removeAttribute('data-hidden');
        if (this.dirtyWhileOpen) {
          this.dirtyWhileOpen = false;
          this.scrollToResults();
        }
        this.drawerTrigger?.focus({ preventScroll: true });
      },
      { signal }
    );
  }

  openDrawer(trigger) {
    if (!this.drawer) return;
    this.closeSort();
    this.drawerTrigger = trigger;
    this.dirtyWhileOpen = false;
    this.drawer.showModal();
  }

  closeDrawer() {
    this.drawer?.close();
  }

  /* ---------- Clicks inside the section ---------- */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const open = target.closest('[data-ir-open-drawer]');
    if (open) return this.openDrawer(open);

    if (target.closest('[data-ir-close-drawer]')) return this.closeDrawer();

    const density = target.closest('[data-ir-cols]');
    if (density) return this.setDensity(density.dataset.irCols);

    const nav = target.closest('a[data-ir-nav]');
    if (nav && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      return this.apply(nav.href, { from: nav });
    }

    const more = target.closest('[data-ir-load-more]');
    if (more && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      return this.loadMore(more);
    }

    if (target.closest('[data-ir-top]')) {
      scrollToTop(0);
      const first = this.querySelector('[data-ir-card] .ir-pcard__title a');
      first?.focus({ preventScroll: true });
    }
  };

  onFormChange = (event) => {
    clearTimeout(this.priceTimer);
    this.apply(this.formUrl());
    void event;
  };

  onFormInput = (event) => {
    if (event.target.type !== 'number') return;
    clearTimeout(this.priceTimer);
    // Typing again supersedes a request that was already sent for a partial value.
    this.controller?.abort();
    this.priceTimer = setTimeout(() => this.apply(this.formUrl()), 700);
  };

  formUrl(sortOverride) {
    const base = new URL(this.dataset.url, window.location.origin);
    const params = new URLSearchParams(base.search);
    if (this.form) {
      const seen = new Set();
      for (const [key, value] of new FormData(this.form)) {
        if (value === '' || key === 'page') continue;
        if (!seen.has(key) && params.has(key)) params.delete(key);
        seen.add(key);
        params.append(key, String(value));
      }
    }
    if (sortOverride) params.set('sort_by', sortOverride);
    const query = params.toString();
    return `${base.pathname}${query ? `?${query}` : ''}`;
  }

  /* ---------- Live rendering ---------- */
  async fetchSection(url) {
    this.controller?.abort();
    this.controller = new AbortController();
    const fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);
    const response = await fetch(fetchUrl.toString(), { signal: this.controller.signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error(String(response.status));
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async apply(url, { from = null } = {}) {
    const target = new URL(url, window.location.origin);
    target.searchParams.delete('page');
    target.searchParams.delete('section_id');
    const id = ++this.requestId;
    const focusContext = this.focusContext(from);
    this.setAttribute('aria-busy', 'true');
    let doc;
    try {
      doc = await this.fetchSection(target.toString());
    } catch (error) {
      if (id === this.requestId) this.removeAttribute('aria-busy');
      if (error?.name === 'AbortError') return;
      window.location.href = target.toString();
      return;
    }
    if (id !== this.requestId) return;
    this.swap(doc);
    this.removeAttribute('aria-busy');

    const state = typeof history.state === 'object' && history.state !== null ? { ...history.state } : {};
    delete state.irPages;
    history.replaceState(state, '', `${target.pathname}${target.search}`);
    this.dispatchCollectionUpdate();
    this.restoreFocus(focusContext);

    if (this.drawer?.open) {
      this.dirtyWhileOpen = true;
      const status = this.querySelector('[data-ir-drawer-status]');
      if (status) {
        status.textContent = '';
        requestAnimationFrame(() => (status.textContent = this.querySelector('[data-ir-count]')?.textContent?.trim() || ''));
      }
    } else if (!focusContext) this.scrollToResults();
  }

  /** Where focus was, so it can be put somewhere sensible after the markup is replaced. */
  focusContext(from) {
    const active = from || document.activeElement;
    if (!(active instanceof Element) || !this.contains(active)) return null;
    if (this.drawer?.contains(active)) {
      return active.matches('[data-ir-clear]') ? { zone: 'drawer-clear' } : null;
    }
    const chips = this.querySelector('[data-ir-chips]');
    if (chips?.contains(active)) {
      const links = [...chips.querySelectorAll('a')];
      return { zone: 'chips', index: links.indexOf(active.closest('a')) };
    }
    if (this.querySelector('[data-ir-results]')?.contains(active)) return { zone: 'results' };
    return null;
  }

  restoreFocus(context) {
    if (!context) return;
    const focus = (el) => el?.focus({ preventScroll: false });
    if (context.zone === 'drawer-clear') {
      focus(this.querySelector('.ir-drawer__apply'));
    } else if (context.zone === 'chips') {
      const links = [...this.querySelectorAll('[data-ir-chips] a')];
      const next = links[Math.min(Math.max(context.index, 0), links.length - 1)];
      focus(next || this.querySelector('[data-ir-open-drawer]'));
    } else if (context.zone === 'results') {
      const heading = this.querySelector('[data-ir-results] h2');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        focus(heading);
      }
    }
  }

  swap(doc) {
    const pick = (selector) => doc.querySelector(selector);
    const replaceInner = (selector) => {
      const next = pick(selector);
      const current = this.querySelector(selector);
      if (next && current) current.innerHTML = next.innerHTML;
    };

    replaceInner('[data-ir-count]');
    this.fitCount();
    replaceInner('[data-ir-chips]');
    replaceInner('[data-ir-drawer-count]');

    const results = this.querySelector('[data-ir-results]');
    const nextResults = pick('[data-ir-results]');
    if (results && nextResults) {
      results.replaceWith(nextResults);
      nextResults.querySelectorAll('[data-ir-item], .ir-grid__item--tile').forEach((item, index) => {
        if (index < 12 && !reduceMotion.matches) {
          item.classList.add('is-new');
          item.style.setProperty('--d', String(index));
        }
      });
    }

    this.swapFilterGroups(pick('[data-ir-filter-groups]'));

    const badge = this.querySelector('[data-ir-filter-badge]');
    const nextBadge = pick('[data-ir-filter-badge]');
    if (badge && nextBadge) {
      badge.textContent = nextBadge.textContent;
      badge.hidden = nextBadge.hidden;
    }
    const clear = this.querySelector('[data-ir-clear]');
    const nextClear = pick('[data-ir-clear]');
    if (clear && nextClear) {
      clear.hidden = nextClear.hidden;
      clear.setAttribute('href', nextClear.getAttribute('href'));
    }

    // Sort state (menu + drawer radios + label)
    const label = this.querySelector('[data-ir-sort-label]');
    const nextLabel = pick('[data-ir-sort-label]');
    if (label && nextLabel) label.textContent = nextLabel.textContent;
    const checked = pick('input[name="sort_by"]:checked')?.value;
    if (checked) {
      this.querySelectorAll('input[name="sort_by"]').forEach((r) => (r.checked = r.value === checked));
      this.querySelectorAll('[data-ir-sort-value]').forEach((o) =>
        o.setAttribute('aria-checked', String(o.dataset.irSortValue === checked))
      );
    }

    this.syncDensity();
    enhanceCards(this);
  }

  /**
   * Updates the filter groups in place: summaries and option lists are refreshed, but a group whose
   * price field is being edited is left alone, and focus returns to the same control.
   */
  swapFilterGroups(nextGroups) {
    const groups = this.querySelector('[data-ir-filter-groups]');
    if (!groups || !nextGroups) return;
    const current = [...groups.querySelectorAll(':scope > [data-ir-fgroup]')];
    const incoming = [...nextGroups.querySelectorAll(':scope > [data-ir-fgroup]')];
    const sameShape =
      current.length === incoming.length && current.every((d, i) => d.dataset.irFgroup === incoming[i].dataset.irFgroup);
    const active = document.activeElement;
    const focusKey = active && groups.contains(active) && active.name ? `${active.name}|${active.value}` : null;

    if (!sameShape) {
      groups.innerHTML = nextGroups.innerHTML;
    } else {
      current.forEach((details, i) => {
        const next = incoming[i];
        const editing = details.contains(active) && active?.type === 'number';
        const summary = details.querySelector(':scope > summary');
        const nextSummary = next.querySelector(':scope > summary');
        if (summary && nextSummary) summary.innerHTML = nextSummary.innerHTML;
        if (editing) return;
        const content = details.querySelector('.ir-fgroup__content');
        const nextContent = next.querySelector('.ir-fgroup__content');
        if (content && nextContent) content.innerHTML = nextContent.innerHTML;
      });
    }

    if (focusKey && !groups.contains(document.activeElement)) {
      const [name, value] = focusKey.split('|');
      [...groups.querySelectorAll('input')].find((i) => i.name === name && i.value === value)?.focus({ preventScroll: true });
    }
  }

  scrollToResults() {
    const results = this.querySelector('[data-ir-results]');
    if (!results) return;
    const offset = headerHeight() + (this.toolbar?.offsetHeight || 0) + 12;
    const top = results.getBoundingClientRect().top;
    if (top < offset || top > window.innerHeight * 0.85) {
      scrollToTop(Math.max(0, scrollTopNow() + top - offset));
    }
  }

  async dispatchCollectionUpdate() {
    await eventsReady;
    const CollectionUpdateEvent = eventsModule?.CollectionUpdateEvent;
    if (!CollectionUpdateEvent) return;
    try {
      const rawId = this.dataset.collectionId;
      const count = () => parseInt(this.querySelector('[data-ir-results]')?.dataset.total || '0', 10);
      const deferred = CollectionUpdateEvent.createPromise();
      const productFilters = CollectionUpdateEvent.parseProductFilters();
      this.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: rawId && /^\d+$/.test(rawId) ? rawId : null, handle: this.dataset.collectionHandle, productsCount: count() },
          ...(productFilters && { productFilters }),
          sortKey: CollectionUpdateEvent.getSortKey(),
          promise: deferred.promise,
        })
      );
      deferred.resolve({ productsCount: count() });
    } catch (_) {
      /* analytics must never break the page */
    }
  }

  /* ---------- Load more ---------- */
  async loadMore(button, { silent = false } = {}) {
    // A filter/sort refresh in flight owns the grid; its result replaces this button anyway.
    if (button.dataset.state === 'busy' || this.hasAttribute('aria-busy')) return false;
    setState(button, 'busy');
    const id = this.requestId;
    const url = new URL(button.href, window.location.origin);
    let doc;
    try {
      doc = await this.fetchSection(url.toString());
    } catch (error) {
      setState(button, null);
      if (error?.name !== 'AbortError' && !silent) window.location.href = url.toString();
      return false;
    }
    if (id !== this.requestId || !button.isConnected) return false;
    const grid = this.querySelector('[data-ir-grid]');
    const items = [...doc.querySelectorAll('[data-ir-grid] > [data-ir-item]')];
    const end = this.querySelector('[data-ir-end]');
    const nextEnd = doc.querySelector('[data-ir-end]');
    if (!items.length && !nextEnd) {
      setState(button, null);
      button.remove();
      return false;
    }
    items.forEach((item, index) => {
      if (!silent && !reduceMotion.matches) {
        item.classList.add('is-new');
        item.style.setProperty('--d', String(index % 12));
      }
    });
    grid?.append(...items);
    if (end && nextEnd) end.replaceWith(nextEnd);
    const page = parseInt(url.searchParams.get('page') || '2', 10);
    const state = typeof history.state === 'object' && history.state !== null ? { ...history.state } : {};
    history.replaceState({ ...state, irPages: page }, '');
    this.syncDensity();
    enhanceCards(this);
    if (!silent) items[0]?.querySelector('.ir-pcard__title a')?.focus({ preventScroll: true });
    return true;
  }

  async restorePages() {
    const pages = history.state?.irPages;
    const savedTop = history.state?.scrollTop;
    if (!pages || pages < 2) return;
    const current = parseInt(this.querySelector('[data-ir-results]')?.dataset.page || '1', 10);
    if (current !== 1) return;
    for (let page = 2; page <= pages; page++) {
      const more = this.querySelector('[data-ir-load-more]');
      if (!more) break;
      // eslint-disable-next-line no-await-in-loop
      const ok = await this.loadMore(more, { silent: true });
      if (!ok) break;
    }
    await scrollReady;
    if (Number.isFinite(savedTop)) requestAnimationFrame(() => scrollToTop(savedTop, false));
  }

  /* ---------- Sticky capsule ---------- */
  async initSticky(signal) {
    const sentinel = this.querySelector('[data-ir-sentinel]');
    if (!sentinel || !this.toolbar || !('IntersectionObserver' in window)) return;
    await scrollReady;
    if (signal.aborted) return;
    const bind = () => {
      if (signal.aborted) return;
      this.stickyObserver?.disconnect();
      const root = scrollModule?.getIntersectionRoot?.() ?? null;
      const top = Math.round(headerHeight());
      this.stickyObserver = new IntersectionObserver(
        ([entry]) => {
          const stuck = !entry.isIntersecting && entry.boundingClientRect.top < top + 2;
          this.toolbar.toggleAttribute('data-stuck', stuck);
          if (!stuck) this.toolbar.removeAttribute('data-hidden');
        },
        { root, rootMargin: `-${top + 1}px 0px 0px 0px`, threshold: 0 }
      );
      this.stickyObserver.observe(sentinel);

      this.scrollTarget?.removeEventListener('scroll', this.onScroll);
      this.scrollTarget = scrollModule?.getScrollEventTarget?.() ?? document;
      this.lastTop = scrollTopNow();
      this.scrollTarget.addEventListener('scroll', this.onScroll, { passive: true, signal });
    };
    bind();
    scrollModule?.scrollContainerMediaQuery?.addEventListener?.('change', bind, { signal });
    let resizeTimer;
    window.addEventListener(
      'resize',
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(bind, 200);
      },
      { signal }
    );
  }

  onScroll = () => {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      const top = scrollTopNow();
      const delta = top - (this.lastTop ?? top);
      this.lastTop = top;
      if (!mobileQuery.matches || !this.toolbar?.hasAttribute('data-stuck')) {
        this.toolbar?.removeAttribute('data-hidden');
        return;
      }
      if (this.drawer?.open || this.toolbar.contains(document.activeElement)) return;
      if (delta > 6 && top > 240) this.toolbar.setAttribute('data-hidden', '');
      else if (delta < -6) this.toolbar.removeAttribute('data-hidden');
    });
  };
}

if (!customElements.get('ir-collection')) customElements.define('ir-collection', IrCollection);

/* ==========================================================================
   Recently viewed
   ========================================================================== */
function renderRecent() {
  document.querySelectorAll('[data-ir-recent]').forEach((block) => {
    let items = [];
    try {
      items = JSON.parse(store.get('ir:recent') || '[]');
    } catch (_) {
      items = [];
    }
    // Same currency AND language, so titles and links match the storefront being browsed.
    const market = `${window.Shopify?.currency?.active || ''}|${window.Shopify?.locale || ''}`;
    const onPage = new Set(
      [...document.querySelectorAll('[data-ir-card] a[data-ir-link]')].map((a) => {
        try {
          return new URL(a.href, window.location.origin).pathname.split('/products/')[1]?.split('/')[0];
        } catch (_) {
          return '';
        }
      })
    );
    items = items.filter((x) => x && x.h && x.u && (!x.m || x.m === market) && !onPage.has(x.h)).slice(0, 10);
    const list = block.querySelector('[data-ir-recent-list]');
    if (!list || items.length < 2) {
      block.hidden = true;
      return;
    }
    list.replaceChildren(
      ...items.map((item) => {
        const [name, ...rest] = String(item.t).split(' — ');
        const base = item.i ? item.i.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '') : '';
        const li = document.createElement('li');
        li.innerHTML = `
          <a class="ir-recent__card" href="${escapeHtml(item.u)}">
            <span class="ir-recent__media">${base ? `<img src="${escapeHtml(sized(base, 400))}" alt="" loading="lazy" width="400" height="500">` : ''}</span>
            <span>
              <span class="ir-recent__name">${escapeHtml(name)}</span>
              ${rest.length ? `<span class="ir-recent__sub">${escapeHtml(rest.join(' — '))}</span>` : ''}
            </span>
            <span class="ir-recent__price">${priceHtml(item.p || '', item.c || '')}</span>
          </a>`;
        return li;
      })
    );
    block.hidden = false;
    const clear = block.querySelector('[data-ir-recent-clear]');
    if (clear && !clear._irBound) {
      clear._irBound = true;
      clear.addEventListener('click', () => {
        store.remove('ir:recent');
        const next = block.nextElementSibling?.querySelector('h2');
        block.hidden = true;
        if (next) {
          next.setAttribute('tabindex', '-1');
          next.focus();
        }
      });
    }
  });
}

/* ==========================================================================
   Boot
   ========================================================================== */
/* Hero category rail: the edge fade (and desktop scrollbar) only when there is more to scroll */
const railObserver =
  'ResizeObserver' in window ? new ResizeObserver((entries) => entries.forEach((entry) => syncRailOverflow(entry.target))) : null;
function syncRailOverflow(list) {
  list.toggleAttribute('data-overflow', list.scrollWidth > list.clientWidth + 1);
}

function boot(root = document) {
  enhanceCards(root);
  root.querySelectorAll?.('[data-ir-rail]').forEach((list) => {
    syncRailOverflow(list);
    railObserver?.observe(list);
  });
  root.querySelectorAll?.('[data-ir-rail-track]').forEach(syncRailArrows);
  renderRecent();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
else boot();

document.addEventListener('shopify:section:load', (event) => boot(event.target));
window.addEventListener('pageshow', (event) => {
  if (event.persisted) renderRecent();
});
